import React, { useEffect, useState } from 'react';
import { Card, Button, Alert, Form, Spinner, Badge, Row, Col, Modal, ProgressBar } from 'react-bootstrap';
import {
  supportsWebUSB,
  supportsWebBluetooth,
  loadPrinterConfig,
  savePrinterConfig,
  clearPrinterConfig,
  pairUsbPrinter,
  pairBluetoothPrinter,
  tryReconnect,
  isConnected,
  printTestReceipt,
  disconnect,
} from '../services/printerService';
import './PrinterSetup.css';

const STEPS = {
  OVERVIEW: 'overview',
  CONNECTION_TYPE: 'connection_type',
  PAIR: 'pair',
  PAPER: 'paper',
  TEST: 'test',
  OPTIONS: 'options',
  DONE: 'done',
};

const STEP_ORDER = [
  STEPS.OVERVIEW,
  STEPS.CONNECTION_TYPE,
  STEPS.PAIR,
  STEPS.PAPER,
  STEPS.TEST,
  STEPS.OPTIONS,
  STEPS.DONE,
];

const PrinterSetup = ({ restaurantUid, restaurantName }) => {
  const [existingConfig, setExistingConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [step, setStep] = useState(STEPS.OVERVIEW);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busy, setBusy] = useState(false);

  // Draft config being built through the wizard
  const [draft, setDraft] = useState({
    connectionType: null,
    vendorId: null,
    productId: null,
    deviceName: '',
    manufacturer: null,
    serialNumber: null,
    deviceId: null,
    paperWidth: 80,
    autoCut: true,
    autoPrint: false,
    cashDrawerKick: false,
  });

  const [testResult, setTestResult] = useState(null); // null | 'pending' | 'ok' | 'fail'
  const [connectedStatus, setConnectedStatus] = useState(false);
  const [removeConfirm, setRemoveConfirm] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const cfg = await loadPrinterConfig(restaurantUid);
        if (cancelled) return;
        setExistingConfig(cfg);
        if (cfg) {
          // Best-effort silent reconnect so existing config shows "Connected" without a click.
          const ok = await tryReconnect(cfg);
          if (!cancelled) setConnectedStatus(ok);
        }
      } catch (err) {
        console.warn('[PrinterSetup] load failed:', err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [restaurantUid]);

  const webUsbSupported = supportsWebUSB();
  const webBtSupported = supportsWebBluetooth();
  const anySupported = webUsbSupported || webBtSupported;

  const resetWizard = () => {
    setStep(STEPS.OVERVIEW);
    setError('');
    setSuccess('');
    setTestResult(null);
    setDraft({
      connectionType: null,
      vendorId: null,
      productId: null,
      deviceName: '',
      manufacturer: null,
      serialNumber: null,
      deviceId: null,
      paperWidth: 80,
      autoCut: true,
      autoPrint: false,
      cashDrawerKick: false,
    });
  };

  const startWizard = () => {
    resetWizard();
    setWizardOpen(true);
  };

  const closeWizard = () => {
    setWizardOpen(false);
  };

  const goNext = () => {
    const idx = STEP_ORDER.indexOf(step);
    if (idx < STEP_ORDER.length - 1) setStep(STEP_ORDER[idx + 1]);
  };

  const goBack = () => {
    const idx = STEP_ORDER.indexOf(step);
    if (idx > 0) setStep(STEP_ORDER[idx - 1]);
  };

  const handlePairUsb = async () => {
    setBusy(true);
    setError('');
    try {
      const meta = await pairUsbPrinter();
      setDraft(d => ({ ...d, ...meta }));
      setStep(STEPS.PAPER);
    } catch (err) {
      if (err.name === 'NotFoundError') {
        setError('No printer selected. Plug the printer into a USB port, click "Pair USB printer" again, and pick it from the list.');
      } else {
        setError(err.message || 'Failed to pair USB printer.');
      }
    } finally {
      setBusy(false);
    }
  };

  const handlePairBluetooth = async () => {
    setBusy(true);
    setError('');
    try {
      const meta = await pairBluetoothPrinter();
      setDraft(d => ({ ...d, ...meta }));
      setStep(STEPS.PAPER);
    } catch (err) {
      if (err.name === 'NotFoundError') {
        setError('No printer selected. Make sure the printer is on and in pairing mode, then try again.');
      } else {
        setError(err.message || 'Failed to pair Bluetooth printer.');
      }
    } finally {
      setBusy(false);
    }
  };

  const handleTestPrint = async () => {
    setBusy(true);
    setError('');
    setTestResult('pending');
    try {
      await printTestReceipt(restaurantName || 'Koda Carte', draft.paperWidth);
      setTestResult('ok');
    } catch (err) {
      setTestResult('fail');
      setError(err.message || 'Test print failed.');
    } finally {
      setBusy(false);
    }
  };

  const handleFinish = async () => {
    setBusy(true);
    setError('');
    try {
      const toSave = {
        connectionType: draft.connectionType,
        vendorId: draft.vendorId,
        productId: draft.productId,
        deviceName: draft.deviceName,
        manufacturer: draft.manufacturer,
        serialNumber: draft.serialNumber,
        deviceId: draft.deviceId,
        paperWidth: draft.paperWidth,
        autoCut: draft.autoCut,
        autoPrint: draft.autoPrint,
        cashDrawerKick: draft.cashDrawerKick,
      };
      await savePrinterConfig(restaurantUid, toSave);
      setExistingConfig(toSave);
      setConnectedStatus(isConnected());
      setSuccess('Printer setup complete.');
      setWizardOpen(false);
    } catch (err) {
      setError(err.message || 'Failed to save printer config.');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    setBusy(true);
    setError('');
    try {
      await clearPrinterConfig(restaurantUid);
      setExistingConfig(null);
      setConnectedStatus(false);
      setRemoveConfirm(false);
      setSuccess('Printer disconnected.');
    } catch (err) {
      setError(err.message || 'Failed to remove printer.');
    } finally {
      setBusy(false);
    }
  };

  const handleReconnect = async () => {
    setBusy(true);
    setError('');
    try {
      const ok = await tryReconnect(existingConfig);
      setConnectedStatus(ok);
      if (!ok) {
        setError('Could not reconnect automatically. Click "Reconfigure" to pair again.');
      } else {
        setSuccess('Printer reconnected.');
      }
    } catch (err) {
      setError(err.message || 'Reconnect failed.');
    } finally {
      setBusy(false);
    }
  };

  const handleToggleAutoPrint = async (value) => {
    if (!existingConfig) return;
    setBusy(true);
    try {
      const next = { ...existingConfig, autoPrint: value };
      await savePrinterConfig(restaurantUid, next);
      setExistingConfig(next);
    } catch (err) {
      setError(err.message || 'Failed to update setting.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Card className="printer-setup-card">
        <Card.Body className="text-center py-5">
          <Spinner animation="border" />
        </Card.Body>
      </Card>
    );
  }

  return (
    <>
      <Card className="printer-setup-card">
        <Card.Header className="account-card-header">
          <h3><i className="bi bi-printer"></i> Receipt Printer</h3>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
          {success && <Alert variant="success" onClose={() => setSuccess('')} dismissible>{success}</Alert>}

          {!anySupported && (
            <Alert variant="warning">
              <strong>Browser not supported.</strong> Thermal printer setup requires
              Chrome or Edge on a desktop or Android device. The POS will fall back
              to the browser print dialog for now.
            </Alert>
          )}

          {existingConfig ? (
            <ConfiguredView
              config={existingConfig}
              connectedStatus={connectedStatus}
              onReconnect={handleReconnect}
              onReconfigure={startWizard}
              onRemove={() => setRemoveConfirm(true)}
              onToggleAutoPrint={handleToggleAutoPrint}
              busy={busy}
            />
          ) : (
            <EmptyView onStart={startWizard} anySupported={anySupported} />
          )}
        </Card.Body>
      </Card>

      <Modal show={wizardOpen} onHide={closeWizard} size="lg" centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-printer me-2"></i>
            Printer Setup
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <WizardProgress step={step} />
          {error && <Alert variant="danger" onClose={() => setError('')} dismissible className="mt-3">{error}</Alert>}
          <div className="mt-3">
            {step === STEPS.OVERVIEW && (
              <OverviewStep onNext={goNext} />
            )}
            {step === STEPS.CONNECTION_TYPE && (
              <ConnectionTypeStep
                draft={draft}
                setDraft={setDraft}
                webUsbSupported={webUsbSupported}
                webBtSupported={webBtSupported}
                onNext={goNext}
                onBack={goBack}
              />
            )}
            {step === STEPS.PAIR && (
              <PairStep
                draft={draft}
                busy={busy}
                onPairUsb={handlePairUsb}
                onPairBluetooth={handlePairBluetooth}
                onBack={goBack}
              />
            )}
            {step === STEPS.PAPER && (
              <PaperStep
                draft={draft}
                setDraft={setDraft}
                onNext={goNext}
                onBack={goBack}
              />
            )}
            {step === STEPS.TEST && (
              <TestStep
                busy={busy}
                testResult={testResult}
                onTestPrint={handleTestPrint}
                onPrintAgain={handleTestPrint}
                onNext={goNext}
                onBack={goBack}
              />
            )}
            {step === STEPS.OPTIONS && (
              <OptionsStep
                draft={draft}
                setDraft={setDraft}
                onNext={goNext}
                onBack={goBack}
              />
            )}
            {step === STEPS.DONE && (
              <DoneStep
                draft={draft}
                busy={busy}
                onFinish={handleFinish}
                onBack={goBack}
              />
            )}
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={removeConfirm} onHide={() => setRemoveConfirm(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Remove printer?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>This disconnects the printer from Koda Carte. Receipts will fall back to the browser print dialog until you set up another printer.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setRemoveConfirm(false)} disabled={busy}>Cancel</Button>
          <Button variant="danger" onClick={handleRemove} disabled={busy}>
            {busy ? <><Spinner size="sm" className="me-1" /> Removing...</> : 'Remove Printer'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

// --- Step components --------------------------------------------------------

function WizardProgress({ step }) {
  const idx = STEP_ORDER.indexOf(step);
  const pct = Math.round((idx / (STEP_ORDER.length - 1)) * 100);
  return (
    <div className="printer-wizard-progress">
      <ProgressBar now={pct} />
      <div className="printer-wizard-step-label">
        Step {idx + 1} of {STEP_ORDER.length}
      </div>
    </div>
  );
}

function OverviewStep({ onNext }) {
  return (
    <div className="printer-step">
      <h5>Let's connect your receipt printer</h5>
      <p className="text-muted">
        In a few quick steps we'll pair your thermal receipt printer, run a test
        print, and choose whether to auto-print receipts on payment.
      </p>
      <ul className="printer-bullets">
        <li>You'll need a <strong>USB</strong> or <strong>Bluetooth</strong> ESC/POS thermal printer (58mm or 80mm).</li>
        <li>For USB, plug the printer into the computer running the POS.</li>
        <li>For Bluetooth, turn on the printer and put it in pairing mode.</li>
      </ul>
      <div className="step-actions">
        <Button variant="primary" onClick={onNext}>
          Get Started <i className="bi bi-arrow-right ms-1"></i>
        </Button>
      </div>
    </div>
  );
}

function ConnectionTypeStep({ draft, setDraft, webUsbSupported, webBtSupported, onNext, onBack }) {
  const pick = (type) => {
    setDraft(d => ({ ...d, connectionType: type }));
  };
  return (
    <div className="printer-step">
      <h5>How is the printer connected?</h5>
      <Row className="g-3 mt-1">
        <Col md={4}>
          <div
            className={`printer-option-card ${draft.connectionType === 'usb' ? 'selected' : ''} ${!webUsbSupported ? 'disabled' : ''}`}
            onClick={() => webUsbSupported && pick('usb')}
          >
            <i className="bi bi-usb-symbol"></i>
            <h6>USB Cable</h6>
            <p>Plug the printer into this computer. Most reliable option.</p>
            {!webUsbSupported && <Badge bg="secondary">Not supported in this browser</Badge>}
          </div>
        </Col>
        <Col md={4}>
          <div
            className={`printer-option-card ${draft.connectionType === 'bluetooth' ? 'selected' : ''} ${!webBtSupported ? 'disabled' : ''}`}
            onClick={() => webBtSupported && pick('bluetooth')}
          >
            <i className="bi bi-bluetooth"></i>
            <h6>Bluetooth</h6>
            <p>Pair wirelessly. Works with most ESC/POS BT printers.</p>
            {!webBtSupported && <Badge bg="secondary">Not supported in this browser</Badge>}
          </div>
        </Col>
        <Col md={4}>
          <div className="printer-option-card disabled">
            <i className="bi bi-wifi"></i>
            <h6>Wi-Fi / Network</h6>
            <p>Printer on the restaurant network.</p>
            <Badge bg="info">Coming soon</Badge>
          </div>
        </Col>
      </Row>
      <div className="step-actions mt-4">
        <Button variant="outline-secondary" onClick={onBack}>Back</Button>
        <Button variant="primary" onClick={onNext} disabled={!draft.connectionType}>
          Continue <i className="bi bi-arrow-right ms-1"></i>
        </Button>
      </div>
    </div>
  );
}

function PairStep({ draft, busy, onPairUsb, onPairBluetooth, onBack }) {
  return (
    <div className="printer-step">
      <h5>Pair with your printer</h5>
      <p className="text-muted">
        When you click <strong>Pair</strong>, your browser will show a device picker.
        Select your printer from the list.
      </p>
      {draft.connectionType === 'usb' && (
        <div className="text-center my-4">
          <Button size="lg" variant="primary" onClick={onPairUsb} disabled={busy}>
            {busy ? <><Spinner size="sm" className="me-2" /> Waiting for device...</> : <><i className="bi bi-usb-symbol me-2"></i>Pair USB Printer</>}
          </Button>
          <p className="small text-muted mt-3">
            Can't find your printer? Make sure it's plugged in, turned on, and that
            no other app (like a system print driver) has claimed it.
          </p>
        </div>
      )}
      {draft.connectionType === 'bluetooth' && (
        <div className="text-center my-4">
          <Button size="lg" variant="primary" onClick={onPairBluetooth} disabled={busy}>
            {busy ? <><Spinner size="sm" className="me-2" /> Waiting for device...</> : <><i className="bi bi-bluetooth me-2"></i>Pair Bluetooth Printer</>}
          </Button>
          <p className="small text-muted mt-3">
            Turn on the printer and put it in pairing mode before clicking above.
          </p>
        </div>
      )}
      <div className="step-actions">
        <Button variant="outline-secondary" onClick={onBack}>Back</Button>
      </div>
    </div>
  );
}

function PaperStep({ draft, setDraft, onNext, onBack }) {
  return (
    <div className="printer-step">
      <h5>Paper size</h5>
      <p className="text-muted">
        Paired with <strong>{draft.deviceName || 'Printer'}</strong>. What paper does it use?
      </p>
      <Row className="g-3 mt-1">
        <Col md={6}>
          <div
            className={`printer-option-card ${draft.paperWidth === 58 ? 'selected' : ''}`}
            onClick={() => setDraft(d => ({ ...d, paperWidth: 58 }))}
          >
            <h6>58mm</h6>
            <p>Compact receipts, ~32 characters per line. Common on portable BT printers.</p>
          </div>
        </Col>
        <Col md={6}>
          <div
            className={`printer-option-card ${draft.paperWidth === 80 ? 'selected' : ''}`}
            onClick={() => setDraft(d => ({ ...d, paperWidth: 80 }))}
          >
            <h6>80mm</h6>
            <p>Standard receipts, ~48 characters per line. Most countertop printers.</p>
          </div>
        </Col>
      </Row>
      <div className="step-actions mt-4">
        <Button variant="outline-secondary" onClick={onBack}>Back</Button>
        <Button variant="primary" onClick={onNext}>
          Continue <i className="bi bi-arrow-right ms-1"></i>
        </Button>
      </div>
    </div>
  );
}

function TestStep({ busy, testResult, onTestPrint, onPrintAgain, onNext, onBack }) {
  return (
    <div className="printer-step">
      <h5>Test print</h5>
      <p className="text-muted">
        Let's send a sample receipt to make sure everything works.
      </p>
      <div className="text-center my-4">
        {testResult === null && (
          <Button size="lg" variant="primary" onClick={onTestPrint} disabled={busy}>
            {busy ? <><Spinner size="sm" className="me-2" /> Printing...</> : <><i className="bi bi-printer me-2"></i>Send Test Print</>}
          </Button>
        )}
        {testResult === 'pending' && (
          <div><Spinner animation="border" /> <p className="mt-2">Sending to printer...</p></div>
        )}
        {testResult === 'ok' && (
          <>
            <Alert variant="success">
              <i className="bi bi-check-circle me-2"></i>
              Test sent to the printer. Did a receipt come out?
            </Alert>
            <div className="d-flex gap-2 justify-content-center mt-3">
              <Button variant="outline-secondary" onClick={onPrintAgain} disabled={busy}>Print Again</Button>
              <Button variant="success" onClick={onNext}>Yes, it printed</Button>
            </div>
          </>
        )}
        {testResult === 'fail' && (
          <>
            <Alert variant="danger">Test print failed. Check the error above and try again.</Alert>
            <Button variant="primary" onClick={onPrintAgain} disabled={busy}>Try Again</Button>
          </>
        )}
      </div>
      <div className="step-actions">
        <Button variant="outline-secondary" onClick={onBack}>Back</Button>
      </div>
    </div>
  );
}

function OptionsStep({ draft, setDraft, onNext, onBack }) {
  return (
    <div className="printer-step">
      <h5>Printing options</h5>
      <Form className="mt-3">
        <Form.Check
          type="switch"
          id="printer-auto-cut"
          label="Auto-cut after each receipt"
          checked={draft.autoCut}
          onChange={(e) => setDraft(d => ({ ...d, autoCut: e.target.checked }))}
        />
        <Form.Text className="text-muted d-block mb-3">
          Sends a cut command at the end of each receipt. Turn off if your printer doesn't have a cutter.
        </Form.Text>
        <Form.Check
          type="switch"
          id="printer-auto-print"
          label="Auto-print receipt on payment"
          checked={draft.autoPrint}
          onChange={(e) => setDraft(d => ({ ...d, autoPrint: e.target.checked }))}
        />
        <Form.Text className="text-muted d-block">
          Automatically prints a receipt as soon as a payment succeeds, no extra click needed.
        </Form.Text>
      </Form>
      <div className="step-actions mt-4">
        <Button variant="outline-secondary" onClick={onBack}>Back</Button>
        <Button variant="primary" onClick={onNext}>
          Continue <i className="bi bi-arrow-right ms-1"></i>
        </Button>
      </div>
    </div>
  );
}

function DoneStep({ draft, busy, onFinish, onBack }) {
  return (
    <div className="printer-step">
      <h5>Ready to save</h5>
      <div className="printer-summary mt-3">
        <Row>
          <Col xs={6}><strong>Connection</strong></Col>
          <Col xs={6}>{draft.connectionType?.toUpperCase() || '—'}</Col>
        </Row>
        <Row>
          <Col xs={6}><strong>Device</strong></Col>
          <Col xs={6}>{draft.deviceName || '—'}</Col>
        </Row>
        <Row>
          <Col xs={6}><strong>Paper width</strong></Col>
          <Col xs={6}>{draft.paperWidth}mm</Col>
        </Row>
        <Row>
          <Col xs={6}><strong>Auto-cut</strong></Col>
          <Col xs={6}>{draft.autoCut ? 'On' : 'Off'}</Col>
        </Row>
        <Row>
          <Col xs={6}><strong>Auto-print on payment</strong></Col>
          <Col xs={6}>{draft.autoPrint ? 'On' : 'Off'}</Col>
        </Row>
      </div>
      <div className="step-actions mt-4">
        <Button variant="outline-secondary" onClick={onBack} disabled={busy}>Back</Button>
        <Button variant="success" onClick={onFinish} disabled={busy}>
          {busy ? <><Spinner size="sm" className="me-1" /> Saving...</> : <><i className="bi bi-check2-circle me-1"></i> Save & Finish</>}
        </Button>
      </div>
    </div>
  );
}

// --- Top-level views --------------------------------------------------------

function EmptyView({ onStart, anySupported }) {
  return (
    <div className="printer-empty-view">
      <i className="bi bi-printer display-4 text-muted"></i>
      <h5 className="mt-3">No printer configured</h5>
      <p className="text-muted">
        Set up a thermal receipt printer to print receipts directly from the POS
        and Payments page.
      </p>
      <Button variant="primary" size="lg" onClick={onStart} disabled={!anySupported}>
        <i className="bi bi-magic me-2"></i>
        Start Printer Setup
      </Button>
    </div>
  );
}

function ConfiguredView({ config, connectedStatus, onReconnect, onReconfigure, onRemove, onToggleAutoPrint, busy }) {
  return (
    <>
      <div className="printer-configured-view">
        <div className="d-flex align-items-start justify-content-between mb-3">
          <div>
            <h5 className="mb-1">{config.deviceName || 'Thermal Printer'}</h5>
            <div className="text-muted small">
              {config.connectionType?.toUpperCase()} · {config.paperWidth}mm paper
            </div>
          </div>
          <div>
            {connectedStatus ? (
              <Badge bg="success"><i className="bi bi-check-circle me-1"></i>Connected</Badge>
            ) : (
              <Badge bg="warning" text="dark"><i className="bi bi-exclamation-circle me-1"></i>Not Connected</Badge>
            )}
          </div>
        </div>

        <Row className="printer-config-details">
          <Col md={6} className="mb-2">
            <div className="small text-muted">Manufacturer</div>
            <div>{config.manufacturer || '—'}</div>
          </Col>
          <Col md={6} className="mb-2">
            <div className="small text-muted">Serial</div>
            <div>{config.serialNumber || '—'}</div>
          </Col>
          <Col md={6} className="mb-2">
            <div className="small text-muted">Auto-cut</div>
            <div>{config.autoCut === false ? 'Off' : 'On'}</div>
          </Col>
          <Col md={6} className="mb-2">
            <div className="small text-muted">Last configured</div>
            <div>{config.updatedAt ? new Date(config.updatedAt).toLocaleString() : '—'}</div>
          </Col>
        </Row>

        <div className="mt-3 pt-3 border-top">
          <Form.Check
            type="switch"
            id="printer-auto-print-toggle"
            label="Auto-print receipt on payment"
            checked={!!config.autoPrint}
            onChange={(e) => onToggleAutoPrint(e.target.checked)}
            disabled={busy}
          />
          <Form.Text className="text-muted">
            Prints automatically as soon as a payment succeeds in the POS.
          </Form.Text>
        </div>

        <div className="d-flex gap-2 mt-4">
          {!connectedStatus && (
            <Button variant="outline-primary" onClick={onReconnect} disabled={busy}>
              <i className="bi bi-arrow-clockwise me-1"></i> Reconnect
            </Button>
          )}
          <Button variant="outline-secondary" onClick={onReconfigure} disabled={busy}>
            <i className="bi bi-gear me-1"></i> Reconfigure
          </Button>
          <Button variant="outline-danger" onClick={onRemove} disabled={busy}>
            <i className="bi bi-trash me-1"></i> Remove
          </Button>
        </div>
      </div>
    </>
  );
}

export default PrinterSetup;
